import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import moment from 'moment';
import { Note, CheckCircle, WarningCircle, UsersThree } from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { IAuditoriaEmpresa, INotasManuais, NotaTipo } from '../types';
import { notaTipoFromLabel, pct } from '../format';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { EmptyState } from '../components/EmptyState';
import { NoteDrawer } from './NoteDrawer';
import { UI } from '../ui';

interface ManuaisScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Cruzamentos: navegar a outro submódulo (Integração, Escritório...). */
  onScreen: (screen: string) => void;
}

/** Lag de lançamento: dias entre emissão e lançamento (parse tolerante). */
const lagDias = (emissao?: string, nota?: string): number | null => {
  const e = moment(emissao, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
  const n = moment(nota, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
  if (!e.isValid() || !n.isValid()) return null;
  return n.diff(e, 'days');
};

/** Cor do lag por faixa de dias. */
const lagColor = (d: number): string => {
  if (d > 30) return UI.neg;
  if (d > 7) return UI.warn;
  return UI.muted;
};

export function ManuaisScreen({ empresa, periodo, onScreen }: ManuaisScreenProps) {
  const { getNotasManuais } = useAuditoriaInteligente();
  const [drawer, setDrawer] = useState<{ codigo: string; tipo: NotaTipo } | null>(null);

  const { data, isFetching } = useQuery<INotasManuais>({
    queryKey: ['ai-notas-manuais', { id: empresa?.id, ...periodo }],
    queryFn: getNotasManuais,
    enabled: !!empresa,
  });

  const lista = useMemo(() => data?.lista ?? [], [data]);
  const porUsuario = useMemo(() => data?.por_usuario ?? [], [data]);

  const resumo = useMemo(() => {
    const integradas = lista.filter(n => n.integrado).length;
    return {
      total: lista.length,
      integradas,
      naoIntegradas: lista.length - integradas,
      usuarios: porUsuario.length,
      pctIntegrado: lista.length ? (integradas / lista.length) * 100 : 0,
    };
  }, [lista, porUsuario]);

  // Evolução: notas manuais por mês (soma dos tipos).
  const evolucao = useMemo(() => {
    const map = new Map<string, number>();
    (data?.evolucao ?? []).forEach(e => map.set(e.mes, (map.get(e.mes) ?? 0) + e.qtd));
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([mes, qtd]) => ({ mes, qtd }));
  }, [data]);

  const maxQt = porUsuario.length ? Math.max(...porUsuario.map(u => u.qtd)) : 0;

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver as notas manuais."
        icon={Note}
      />
    );
  }

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Total manuais"
          value={resumo.total}
          icon={Note}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Quantidade de notas lançadas manualmente no Domínio (origem_reg=0) no período. Volume alto indica problema de processo."
              dados="extract_notas_manual (filtra por dt_emissao no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Integradas"
          value={resumo.integradas}
          sub={`${pct(resumo.pctIntegrado)} do total`}
          icon={CheckCircle}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Notas manuais que possuem integração fiscal-contábil registrada (valor > 0). Foram efetivamente integradas à contabilidade."
              dados="extract_notas_manual ⋈ extract_integracao_fiscal_contabil (por external_id + codigo_nota + tipo)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Não integradas"
          value={resumo.naoIntegradas}
          sub="abrir Integração →"
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          isLoading={isFetching}
          onClick={() => onScreen('integracao')}
          info={
            <InfoBloco
              oque="Notas manuais sem integração fiscal-contábil (sem registro na IFC ou valor=0). São o total de manuais menos as integradas."
              dados="extract_notas_manual sem match em extract_integracao_fiscal_contabil (ifc.valor > 0)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Usuários"
          value={resumo.usuarios}
          icon={UsersThree}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Número de usuários distintos do Domínio que lançaram notas manualmente no período (codi_usu únicos)."
              dados="extract_notas_manual.codi_usu (agregado por usuário)"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      {evolucao.length > 0 && (
        <Box
          bg={UI.glass}
          border="1px solid"
          borderColor={UI.glassBorder}
          borderRadius="16px"
          backdropFilter={UI.glassBlur}
          boxShadow={UI.glassShadow}
          p={5}
          mb={5}
        >
          <Text fontSize="13px" fontWeight={800} mb={3} color={UI.ink}>
            Notas manuais por mês
          </Text>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={evolucao}>
              <defs>
                <linearGradient id="aiManual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
              <YAxis fontSize={11} stroke={UI.muted} allowDecimals={false} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="qtd"
                name="Notas manuais"
                stroke="#8B5CF6"
                fill="url(#aiManual)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Grid templateColumns={{ base: '1fr', lg: '3fr 2fr' }} gap={5}>
        <Box
          borderRadius="16px"
          overflow="hidden"
          bg="rgba(255,255,255,0.7)"
          backdropFilter="blur(14px) saturate(160%)"
          border="1px solid rgba(255,255,255,0.85)"
          boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
        >
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>NF / Série</Th>
                  <Th>Tipo</Th>
                  <Th>Data</Th>
                  <Th isNumeric>Lag</Th>
                  <Th>Usuário</Th>
                  <Th>Integrado?</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isFetching && (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}
                {!isFetching &&
                  lista.map(n => (
                    <Tr
                      key={`${n.codigo_nota}-${n.numero}`}
                      cursor={notaTipoFromLabel(n.tipo_nota) ? 'pointer' : 'default'}
                      _hover={{ bg: UI.rowHover }}
                      onClick={() => {
                        const tipo = notaTipoFromLabel(n.tipo_nota);
                        if (tipo) setDrawer({ codigo: n.codigo_nota, tipo });
                      }}
                      title={notaTipoFromLabel(n.tipo_nota) ? 'Ver detalhes da nota' : undefined}
                    >
                      <Td fontWeight={700}>
                        {n.numero}
                        {n.serie ? ` / ${n.serie}` : ''}
                      </Td>
                      <Td>{n.tipo_nota}</Td>
                      <Td>{n.dt_nota}</Td>
                      <Td isNumeric>
                        {(() => {
                          const l = lagDias(n.dt_emissao, n.dt_nota);
                          if (l === null) return '—';
                          return (
                            <Text as="span" color={lagColor(l)} fontWeight={l > 7 ? 700 : 400}>
                              {l}d
                            </Text>
                          );
                        })()}
                      </Td>
                      <Td>
                        <Badge
                          style={{ backgroundColor: '#EFF2F6', color: UI.muted }}
                          borderRadius="full"
                          px={2.5}
                          py={0.5}
                          fontSize="11px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          {n.codi_usu}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          style={
                            n.integrado
                              ? { backgroundColor: UI.posSoft, color: UI.pos }
                              : { backgroundColor: UI.negSoft, color: UI.neg }
                          }
                          borderRadius="full"
                          px={2.5}
                          py={0.5}
                          fontSize="11.5px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          {n.integrado ? 'Sim' : 'Não'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                {!isFetching && lista.length === 0 && (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8} color={UI.muted}>
                      Nenhuma nota manual no período.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          bg={UI.glass}
          border="1px solid"
          borderColor={UI.glassBorder}
          borderRadius="16px"
          backdropFilter={UI.glassBlur}
          boxShadow={UI.glassShadow}
          p={5}
        >
          <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
            Por usuário
          </Text>
          {porUsuario.length === 0 && (
            <Text fontSize="13px" color={UI.muted}>
              Sem lançamentos manuais no período.
            </Text>
          )}
          {porUsuario.map(u => (
            <Box key={u.usuario} mb={3.5}>
              <Flex justify="space-between" mb={1.5} fontWeight={700} fontSize="13px">
                <Text>{u.usuario}</Text>
                <Text>{u.qtd} notas</Text>
              </Flex>
              <Box h="7px" borderRadius="6px" bg={UI.border} overflow="hidden">
                <Box
                  h="100%"
                  borderRadius="6px"
                  bg="#8B5CF6"
                  w={`${maxQt ? (u.qtd / maxQt) * 100 : 0}%`}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Grid>

      {drawer && empresa && (
        <NoteDrawer
          empresaId={empresa.id}
          tipo={drawer.tipo}
          codigo={drawer.codigo}
          isOpen={!!drawer}
          onClose={() => setDrawer(null)}
        />
      )}
    </Box>
  );
}
